import { AlertTriangle, Check, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Banner shown at top of the filtered map detail when the system found
 * surgeons that look like they belong to the user's team but were
 * registered as non-team. Lets the user promote them with one click.
 */
export function PendingTeamPromotions({ items, onPromote, onReject, addingSurgeon }) {
  if (!items || items.length === 0) return null;
  const total = items.reduce((s, p) => s + p.occurrences, 0);
  return (
    <div
      className="bg-emerald-50 border-2 border-emerald-400 rounded-md px-4 py-3 no-print shadow-sm"
      data-testid="pending-promotions-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900">
            {total} cirurgia(s) pertencem à sua equipe, mas o cirurgião está cadastrado como <strong>não-equipe</strong>
          </p>
          <p className="text-xs text-emerald-800 mt-1">
            Clique em <strong>"Promover a equipe"</strong> para marcar o(s) cirurgião(ões) como equipe, salvar a variação de nome como apelido e trazer as linhas para o mapa filtrado automaticamente.
          </p>
          <ul className="mt-2.5 space-y-2">
            {items.map((p) => (
              <li
                key={p.surgeon_id}
                className="flex items-center justify-between gap-2 bg-white border border-emerald-300 rounded-md px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{p.full_name}</div>
                  <div className="text-xs text-slate-600">
                    {p.occurrences}x · no mapa como:{" "}
                    {p.raw_names.map((rn) => (
                      <span
                        key={rn}
                        className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mx-0.5 text-[11px] font-mono"
                      >
                        {rn}
                      </span>
                    ))}
                    {p.was_fuzzy && (
                      <span className="ml-2 text-[10px] text-amber-700">
                        (aproximação {Math.round(p.score)}%)
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs shrink-0"
                  onClick={() => onPromote(p.surgeon_id, p.full_name, p.raw_names[0])}
                  disabled={addingSurgeon === p.surgeon_id}
                  data-testid={`btn-promote-banner-${p.surgeon_id}`}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" />{" "}
                  {addingSurgeon === p.surgeon_id ? "Promovendo..." : "Promover a equipe"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-50 shrink-0"
                  onClick={() => onReject(p.raw_names[0])}
                  data-testid={`btn-reject-team-${p.surgeon_id}`}
                  title="Marcar este nome como NÃO sendo da sua equipe — para sempre"
                >
                  Não é da equipe
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


export function PendingProcedureVariations({ items, onAccept, onReject, busyKey }) {
  if (!items || items.length === 0) return null;
  const total = items.reduce((s, p) => s + p.occurrences, 0);
  return (
    <div
      className="bg-violet-50 border-2 border-violet-400 rounded-md px-4 py-3 no-print shadow-sm"
      data-testid="pending-proc-variations-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-violet-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-violet-900">
            {total} procedimento(s) no mapa parecem ser variação(ões) de procedimento(s) cadastrado(s)
          </p>
          <p className="text-xs text-violet-800 mt-1">
            Se for a mesma cirurgia (para fins de faturamento), clique em <strong>"Sim, é o mesmo"</strong> — o sistema salva como apelido e reconhece automaticamente nas próximas vezes. Se for diferente, clique em <strong>"Não"</strong> e a sugestão não volta a aparecer.
          </p>
          <ul className="mt-2.5 space-y-2">
            {items.map((v) => {
              const k = `proc-${v.procedure_id}-${v.raw_name}`;
              const busy = busyKey === k;
              return (
                <li
                  key={`${v.procedure_id}-${v.raw_name_norm}`}
                  className="flex items-center justify-between gap-2 bg-white border border-violet-300 rounded-md px-3 py-2 text-sm flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-900">
                      No mapa:{" "}
                      <strong className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[12px]">
                        {v.raw_name}
                      </strong>
                      <span className="mx-1.5 text-slate-400">→</span>
                      Cadastrado: <strong>{v.procedure_name}</strong>
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {v.occurrences}x no mapa · score {Math.round(v.score)}%
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      className="bg-violet-700 hover:bg-violet-800 text-white h-8 text-xs"
                      onClick={() => onAccept(v.procedure_id, v.procedure_name, v.raw_name)}
                      disabled={busy}
                      data-testid={`btn-accept-proc-var-${v.procedure_id}`}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" /> {busy ? "..." : "Sim, é o mesmo"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
                      onClick={() => onReject(v.procedure_id, v.procedure_name, v.raw_name)}
                      disabled={busy}
                      data-testid={`btn-reject-proc-var-${v.procedure_id}`}
                    >
                      Não
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}


const ROLE_LABEL = {
  primary: "Cirurgião",
  assistant: "Auxiliar",
  anesthesiologist: "Anestesista",
};

export function AmbiguousMatches({ items, onConfirm, onReject }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 no-print"
      data-testid="ambiguous-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900">
            {items.length} variação(ões) de nome detectada(s) — confirme se é a mesma pessoa
          </p>
          <p className="text-xs text-blue-800 mt-1">
            O mapa trouxe o nome com uma grafia levemente diferente do cadastro. Se for a mesma pessoa,
            clique em <strong>"Sim, é a mesma"</strong> — fica salvo como apelido e nas próximas vezes reconhece automaticamente.
          </p>
          <ul className="mt-2 space-y-2">
            {items.map((a) => {
              const entityId = a.entity_id || a.surgeon_id;
              const entityName = a.entity_name || a.surgeon_name;
              const entityType = a.entity_type || "surgeon";
              const roleLabel = ROLE_LABEL[a.role] || "Pessoa";
              return (
                <li
                  key={`${entityId}-${a.raw_name}`}
                  className="bg-white border border-blue-300 rounded-md px-3 py-2 text-sm flex flex-wrap items-center gap-2"
                  data-testid={`ambig-${a.raw_name.replace(/\s+/g, "-")}`}
                >
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{roleLabel}</span>
                  <span className="text-[11px] uppercase tracking-wider text-slate-500">No mapa:</span>
                  <span className="font-medium text-slate-900">{a.raw_name}</span>
                  <span className="text-[11px] text-slate-400">· {a.occurrences}x</span>
                  <span className="text-[11px] text-slate-500 ml-2">→ parece ser</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-300 font-medium">
                    {entityName}
                  </span>
                  <span className="text-[10px] text-slate-400">({a.score}%)</span>
                  <Button
                    size="sm"
                    className="h-7 bg-emerald-700 hover:bg-emerald-800 text-white text-xs ml-auto"
                    onClick={() => onConfirm(entityId, a.raw_name, entityType)}
                    data-testid={`btn-confirm-variation-${a.raw_name.replace(/\s+/g, "-")}`}
                  >
                    ✓ Sim, é a mesma
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
                    onClick={() => onReject(entityId, entityType, a.raw_name)}
                    data-testid={`btn-reject-variation-${a.raw_name.replace(/\s+/g, "-")}`}
                    title="Não é a mesma pessoa — não sugerir esta associação novamente"
                  >
                    Não, é outro
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}


export function PendingSurgeons({ items, onConfirm, onRegisterSingle, onIgnore, onBulkRegister }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 no-print"
      data-testid="pending-surgeons-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            {items.length} cirurgião(ões) não cadastrado(s) encontrado(s) no mapa
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Nomes que aparecem no mapa ao lado da sua equipe. Se for a mesma pessoa com outro apelido
            ou grafia, clique em <strong>"É o mesmo?"</strong> — fica salvo como apelido pra próxima vez.
          </p>
          <ul className="mt-2 space-y-2">
            {items.map((p) => (
              <li
                key={p.name}
                className="bg-white border border-amber-300 rounded-md px-3 py-2 text-sm flex flex-wrap items-center gap-2"
              >
                <span className="font-medium text-slate-800">{p.name}</span>
                <span className="text-[11px] text-slate-500">· {p.occurrences}x</span>
                {p.suggested_match && (
                  <>
                    <span className="text-[11px] text-slate-500 ml-2">→ parece ser</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                      {p.suggested_match.full_name}
                    </span>
                    <span className="text-[10px] text-slate-400">({p.suggested_match.score}%)</span>
                    <Button
                      size="sm"
                      className="h-7 bg-emerald-700 hover:bg-emerald-800 text-white text-xs ml-auto"
                      onClick={() => onConfirm(p.suggested_match.id, p.name)}
                      data-testid={`btn-confirm-alias-${p.name.replace(/\s+/g, "-")}`}
                    >
                      ✓ É o mesmo
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => onRegisterSingle(p.name)}
                      data-testid={`btn-register-single-${p.name.replace(/\s+/g, "-")}`}
                    >
                      Não, é outro
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => onIgnore(p.name)}
                      data-testid={`btn-ignore-pending-${p.name.replace(/\s+/g, "-")}`}
                      title="Não é da minha equipe — ignorar para sempre"
                    >
                      Ignorar (não é da equipe)
                    </Button>
                  </>
                )}
                {!p.suggested_match && (
                  <div className="ml-auto flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => onRegisterSingle(p.name)}
                    >
                      <UserPlus className="h-3 w-3 mr-1" /> Cadastrar novo
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => onIgnore(p.name)}
                      data-testid={`btn-ignore-pending-${p.name.replace(/\s+/g, "-")}`}
                      title="Não é da minha equipe — ignorar para sempre"
                    >
                      Ignorar (não é da equipe)
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={onBulkRegister}
              data-testid="btn-bulk-register-pending"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Cadastrar todos como não-equipe
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
